import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useRemediation } from '../../context/RemediationContext';
import { getRiskLevel } from '../../data/helpers';

const { FiTrendingUp, FiTrendingDown, FiMinus } = FiIcons;

const LearnerRiskTable = () => {
  const { learners, records } = useRemediation();

  const enriched = learners.map(l => {
    const lRecords = records.filter(r => r.learnerId === l.id);
    const failures = lRecords.length;
    const completed = lRecords.filter(r => r.status === 'Completed').length;
    const pending = lRecords.filter(r => r.status === 'Pending' || r.status === 'In Progress').length;
    const overdue = lRecords.filter(r => r.status === 'Overdue').length;
    const totalDelta = lRecords.reduce((a, b) => a + b.riskScoreDelta + (b.riskAdjustmentDelta || 0), 0);
    return { ...l, failures, completed, pending, overdue, totalDelta };
  }).sort((a, b) => b.phishingRiskScore - a.phishingRiskScore);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700">Learner Risk Overview</h3>
        <p className="text-xs text-slate-500 mt-0.5">Live risk scores with remediation history</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <th className="p-4">Learner</th>
              <th className="p-4">Risk Score</th>
              <th className="p-4">Failures</th>
              <th className="p-4">Completed</th>
              <th className="p-4">Overdue</th>
              <th className="p-4">Net Risk Δ</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {enriched.map(l => {
              const risk = getRiskLevel(l.phishingRiskScore);
              const color = l.phishingRiskScore < 20 ? '#10b981' : l.phishingRiskScore < 50 ? '#f59e0b' : '#ef4444';
              return (
                <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {l.firstName[0]}{l.lastName[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{l.firstName} {l.lastName}</p>
                        <p className="text-xs text-slate-400">{l.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${l.phishingRiskScore}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-sm font-bold" style={{ color }}>{l.phishingRiskScore}</span>
                    </div>
                  </td>
                  <td className="p-4 text-slate-700 font-medium">{l.failures}</td>
                  <td className="p-4">
                    <span className="text-emerald-600 font-semibold">{l.completed}</span>
                  </td>
                  <td className="p-4">
                    {l.overdue > 0 ? <span className="text-red-600 font-semibold">{l.overdue}</span> : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <SafeIcon icon={l.totalDelta > 0 ? FiTrendingUp : l.totalDelta < 0 ? FiTrendingDown : FiMinus}
                        className={l.totalDelta > 0 ? 'text-red-500' : l.totalDelta < 0 ? 'text-emerald-500' : 'text-slate-400'} />
                      <span className={`text-xs font-semibold ${l.totalDelta > 0 ? 'text-red-600' : l.totalDelta < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {l.totalDelta > 0 ? '+' : ''}{l.totalDelta}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${risk.color}`}>
                      {risk.label} Risk
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LearnerRiskTable;